import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useAsync, useAsyncFn } from 'react-use';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { ibeKpiBusinessPerformancesRead } from 'actions/kpi/beKpiBusinessPerformance.action';
import { ibeKpiPersonalPerformanceRead } from 'actions/kpi/beKpiPersonalPerformance.action';
import { ibeKpiStylistsPerformancesRead } from 'actions/kpi/beKpiStylistsPerformance.action';
import { abeStylists } from 'actions/stylist/beSalonStylists.action';
import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import generateScores from 'bl/kpi/generateScores.bl';
import Permitted from 'components/core/Permitted.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import KpiCsvDownloadButton from 'components/kpi/KpiCsvDownloadButton.component';
import KpiHowToTooltip from 'components/kpi/KpiHowToTooltip.component';
import KpiSplash from 'components/kpi/KpiSplash.component';
import KpiSummaryStaffSelector from 'components/kpi/KpiSummaryStaffSelector.component';
import RangeFilter from 'components/kpi/RangeFilter.component';
import ScoreTable from 'components/kpi/ScoreTable.component';
import FLAG from 'config/flag.singleton';
import PERM from 'constants/core/perm.const';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useCustomPermission } from 'providers/Access.provider';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectStylistList } from 'selectors/salonInfo.selectors';
import alen from 'utils/arrays/alen.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import sleep from 'utils/functions/sleep.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';
import isObjectShallowEqual from 'utils/predicates/object/isObjectShallowEqual.util';

import CN from './KpiSummary.module.scss';

const L = logger('KpiSummary');

const DISPLAY_KPI_SPLASH = false;
const USE_MULTIPLE_COMPARE_ALERT = false;
const USE_SPAMMY_LOG = fromAnyToBool(FLAG.loggerSpam);

const SUBJECT = Object.freeze({
  none: 'none',
  business: 'business',
  stylist: 'stylist',
});

const KpiSummary = ({
  stylists: stylistsFromProps,
  abeStylists,
  ibeKpiStylistsPerformancesRead,
  ibeKpiBusinessPerformancesRead,
  ibeKpiPersonalPerformanceRead,
}) => {
  const activeStylistsFromProps = filterActiveStylists(stylistsFromProps);

  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  const isPersonalPermitted = useCustomPermission(PERM.canViewKpiReportsPersonal);
  const isAllStaffPermitted = useCustomPermission(PERM.canViewKpiReportsAllStaff);

  const linkToEditKpi = linkTo({ pattern: LINK.hrEditKpi, params: { salonId }, fault: '#' });

  const scoreTableRef = useRef(null);

  const handlePrintKpi = useReactToPrint({ content: () => scoreTableRef.current });

  const [localSubject, setLocalSubject] = useStateWhileMounted(SUBJECT.business);
  const [localRanges, setLocalRanges] = useStateWhileMounted(null);
  const [localIds, setLocalIds] = useStateWhileMounted(null);

  const [scores, setScores] = useStateWhileMounted(null);

  // NOTE: maybe after they fix the bug in the library, there will be no need for this manual tracking
  // SEE: https://github.com/streamich/react-use/issues/2126
  const { val: isReallyLoading, yay: startLoading, nay: stopLoading } = useTip(false);

  // NOTE: we show a message in a modal if we have quick buttons selected and more than one stylist
  const { val: isMultiCompareModalOpen, nay: closeMultiCompareModal, yay: showMultiCompareModal } = useTip(false);

  const rStylists = useAsync(async () => {
    const result = await abeStylists();

    return filterActiveStylists(result?.is?.ok ? result?.json : []);
  }, [abeStylists]);

  const stylists = rStylists.value ?? activeStylistsFromProps;
  const stylistsCount = alen(stylists);
  const localIdsCount = alen(localIds);

  const [rScores, handleFetchScores] = useAsyncFn(
    async (ev) => {
      // NOTE: sleeping here is an attempt to give the UI some time to finish its work/animations
      await sleep(DELAY.fast);

      // some data may come from the ev, but the rest from local state

      const subject = ev?.subject ?? localSubject ?? null;
      const ranges = Array.isArray(ev?.ranges) ? ev?.ranges : localRanges ?? null;
      const ids = Array.isArray(ev?.ids) ? ev?.ids : localIds ?? null;

      L.debug('handleFetchScores()', ev?.source, { ids, ranges, subject, ev, localIds, localRanges, localSubject });

      if (isNotFullArray(ranges)) return;
      if (isNotFullArray(ids) && SUBJECT.stylist === subject) return;

      const ibeKpiPerformancesRead =
        isPersonalPermitted && !isAllStaffPermitted
          ? ibeKpiPersonalPerformanceRead
          : SUBJECT.business === subject
            ? ibeKpiBusinessPerformancesRead
            : ibeKpiStylistsPerformancesRead;

      const options = ranges.map((r) => ({ ids, kind: r?.kind, bgn: r?.bgnZulu, end: r?.endZulu }));

      const settled = await Promise.allSettled(
        options.map((o) =>
          o?.bgn && o?.end ? ibeKpiPerformancesRead(o) : Promise.reject(`Invalid range from: ${o?.bgn} to: ${o?.end}`),
        ),
      );

      L.debug('handleFetchScores()', ev?.source, settled);

      setScores(generateScores({ options, settled }));
    },
    [
      ibeKpiBusinessPerformancesRead,
      ibeKpiPersonalPerformanceRead,
      ibeKpiStylistsPerformancesRead,
      isAllStaffPermitted,
      isPersonalPermitted,
      localIds,
      localRanges,
      localSubject,
      setScores,
    ],
  );

  const [, handleBusiness] = useAsyncFn(async () => {
    setLocalIds(null);
    setLocalSubject(SUBJECT.business);

    startLoading();
    const result = handleFetchScores({ subject: SUBJECT.business, ids: null, source: 'handleBusiness' });
    result.finally(stopLoading);
  }, [handleFetchScores, setLocalIds, setLocalSubject, startLoading, stopLoading]);

  const [, handleRange] = useAsyncFn(
    async (ev) => {
      const ranges = ev?.intervals ?? null;
      setLocalRanges(ranges);

      if (USE_MULTIPLE_COMPARE_ALERT && (stylistsCount === localIdsCount || localIdsCount > 1)) {
        showMultiCompareModal();
      }

      startLoading();
      const result = handleFetchScores({ ranges, source: 'handleRange' });
      result.finally(stopLoading);
    },
    [handleFetchScores, localIdsCount, setLocalRanges, showMultiCompareModal, startLoading, stopLoading, stylistsCount],
  );

  const [, handleApply] = useAsyncFn(
    async (ev) => {
      const ids = ev?.ids ?? null;
      const subject = ev?.isMobile && ev?.isTotalBusiness ? SUBJECT.business : SUBJECT.stylist;

      setLocalIds((prev) => {
        if (prev === ids) return prev;
        if (isObjectShallowEqual(prev, ids)) return prev;
        return ids;
      });

      setLocalSubject(subject);

      await handleFetchScores({ subject, ids, source: 'handleApply' });
      startLoading();

      const result = handleFetchScores({ subject, ids, source: 'handleApply' });
      result.finally(stopLoading);
    },
    [handleFetchScores, setLocalIds, setLocalSubject, startLoading, stopLoading],
  );

  useEffect(() => void windowScrollToPosition(0, 0), []);

  const isLoading = fromAnyToBool(isReallyLoading || rStylists.loading || rScores.loading);

  const error = rStylists.error || rScores.error;

  if (error) {
    L.warn('()', isReallyLoading, isLoading, error, stylists, scores);
  } else if (USE_SPAMMY_LOG) L.debug('()', isReallyLoading, isLoading, error, stylists, scores);

  if (DISPLAY_KPI_SPLASH) {
    return <KpiSplash />;
  }

  if (!isPersonalPermitted && !isAllStaffPermitted) {
    return (
      <div
        data-bem="KpiSummary--not-permitted"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <br />
        <AlertPanel tag="AlertPanel" variant="warningPanel">
          <div className={CN.alertText}>
            {t('staffKpisReports.kpiReports.alertMessage.title')}
            <br />
            {t('staffKpisReports.kpiReports.alertMessage.description')}
          </div>
        </AlertPanel>
      </div>
    );
  }

  return (
    <div
      data-bem="KpiSummary"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="KpiSummary__panel" className={CN.panelMobile}>
        <div data-bem="KpiSummary__toolbar" className={CN.panel}>
          <div className={CN.myKpiToolbar}>
            <Permitted value={PERM.canViewKpiReportsPersonal}>
              <div className={CN.myKpiTitle}>
                <div className={CN.iconContainer}>
                  <Icon variant="userGroup" width="xl" />
                </div>
                {t('staffKpisReports.kpiReports.myKpi')}
              </div>
            </Permitted>
            <div className={CN.personalKpiButtons}>
              <Permitted value={PERM.canEditKpisPersonal}>
                <div className={CN.editKpiButtonContainer}>
                  <ResponsiveBox.Desktop>
                    <NavLink to={linkToEditKpi}>
                      <Button variant="primaryForce" onClick={alwaysNoop}>
                        <span className={CN.navLinkColor}>{t('staffKpisReports.kpiReports.customiseKpi')}</span>
                      </Button>
                    </NavLink>
                  </ResponsiveBox.Desktop>
                  <ResponsiveBox.Mobile>
                    <NavLink to={linkToEditKpi}>
                      <span className={CN.editMyKpiLink}>{t('staffKpisReports.kpiReports.editKpi')}</span>
                    </NavLink>
                  </ResponsiveBox.Mobile>
                </div>
              </Permitted>

              <Permitted value={PERM.canViewKpiReportsPersonal}>
                <div className={CN.spaceQuestionButton} data-bem="KpiSummary__KpiHowToTooltip--Personal">
                  <KpiHowToTooltip variant="iconOnly" />
                </div>
                <div className={CN.hidePrintMobile}>
                  <Button variant="bin" onClick={handlePrintKpi}>
                    <div className={CN.pdfInnerContainer}>
                      <Icon variant="printer" width="lg" />
                    </div>
                  </Button>
                </div>
              </Permitted>
            </div>
          </div>
          <div className={CN.flex}>
            <div className={CN.container}>
              <div data-bem="KpiSummary__title" className={CN.titleContainer}>
                <Permitted value={PERM.canViewKpiReportsAllStaff}>
                  <div className={CN.title}>
                    <div className={CN.iconContainer}>
                      <Icon variant="userGroup" width="xl" />
                    </div>
                    {t('staffKpisReports.kpiReports.kpisFor')}
                  </div>
                </Permitted>

                <Permitted value={PERM.canViewKpiReportsAllStaff}>
                  <div className={CN.toolbarButtonsTablet} data-bem="KpiSummary__KpiHowToTooltip--AllStaff--iconOnly">
                    <NavLink to={linkToEditKpi} className={CN.navLinkEditKpis}>
                      {t('staffKpisReports.kpiReports.editKpi')}
                    </NavLink>
                    <KpiHowToTooltip variant="iconOnly" />
                  </div>
                </Permitted>
              </div>

              <div data-bem="KpiSummary__switches" className={CN.wrapper}>
                <Permitted value={PERM.canViewKpiReportsAllStaff}>
                  <div className={CN.totalBusinessDisplay}>
                    <ToggleSwitch
                      tag="KpiSummary__total-business"
                      name={t('staffKpisReports.kpiReports.totalBusiness')}
                      size="small"
                      onClick={handleBusiness}
                      selected={SUBJECT.business === localSubject}
                    >
                      <Icon variant="building" width="sm" />
                    </ToggleSwitch>
                  </div>

                  <div className={CN.separator}>{t('common.or').toUpperCase()}</div>

                  <KpiSummaryStaffSelector
                    clearOnDeselect
                    displaySelectAll={false}
                    stylists={stylists}
                    onApply={handleApply}
                    selected={SUBJECT.business !== localSubject}
                    isLoading={rStylists.loading}
                  />
                </Permitted>
              </div>
            </div>

            <div className={CN.toolbarButtonsDesktop}>
              <Permitted value={PERM.canEditKpisAllStaff}>
                <KpiCsvDownloadButton localRanges={localRanges} />
              </Permitted>
              <div className={CN.navLinkContainerDesktop}>
                <Permitted value={PERM.canEditKpisAllStaff}>
                  <NavLink to={linkToEditKpi}>
                    <Button variant="primaryForce" onClick={alwaysNoop}>
                      <span className={CN.navLinkColor}>{t('staffKpisReports.kpiReports.customiseKpi')}</span>
                    </Button>
                  </NavLink>
                </Permitted>
              </div>

              <Permitted value={PERM.canViewKpiReportsAllStaff}>
                <div className={CN.space} data-bem="KpiSummary__KpiHowToTooltip--AllStaff--iconOnly">
                  <KpiHowToTooltip variant="iconOnly" />
                </div>

                <div className={CN.printButtonAllStaff} data-bem="KpiSummary__printButton--AllStaff">
                  <Button variant="bin" onClick={handlePrintKpi}>
                    <div className={CN.pdfInnerContainer}>
                      <Icon variant="printer" width="lg" />
                    </div>
                  </Button>
                </div>
              </Permitted>
            </div>
          </div>
        </div>

        <RangeFilter onChange={handleRange} />

        {isMultiCompareModalOpen && (
          <SalonModal tag="KpiSummary__modal--kpi" isOpen withFooter>
            <SalonModal.Body>
              <IconStatus variant="success" width="lg">
                <Icon variant="clipboardDoc" width="l1" />
              </IconStatus>
              <div className={CN.modalContent}>{t('staffKpisReports.kpiReports.summaryModal.text')}</div>
            </SalonModal.Body>
            <SalonModal.Footer>
              <Button variant="primaryForce" onClick={closeMultiCompareModal}>
                <span className={CN.buttonGap}>{t('staffKpisReports.kpiReports.summaryModal.button')}</span>
              </Button>
            </SalonModal.Footer>
          </SalonModal>
        )}
      </div>

      {isLoading ? (
        <InlineSpinner variant="big" text={t('staffKpisReports.kpiReports.loadingText')} />
      ) : (
        <ScoreTable value={scores} ref={scoreTableRef} />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  stylists: selectStylistList(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      abeStylists,
      ibeKpiStylistsPerformancesRead,
      ibeKpiBusinessPerformancesRead,
      ibeKpiPersonalPerformanceRead,
    },
    dispatch,
  );

const KpiSummaryHoc = connect(mapStateToProps, mapDispatchToProps)(KpiSummary);

export default KpiSummaryHoc;
